import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, Star, MessageCircle } from 'lucide-react';

const packages = [
  {
    name: "Discovery",
    subtitle: "Single Ceremony",
    price: "R$300",
    description: "Perfect for first-time seekers",
    features: [
      "One sacred ceremony",
      "Pre-ceremony consultation",
      "Integration session",
      "Traditional meals included",
      "Basic accommodation"
    ],
    highlighted: false
  },
  {
    name: "Transformation",
    subtitle: "3-Day Retreat",
    price: "R$900",
    description: "The complete journey",
    features: [
      "Three sacred ceremonies",
      "Full preparation program",
      "Daily integration circles",
      "Jungle meditation walks",
      "Premium accommodation",
      "All meals included",
      "Traditional healing bath"
    ],
    highlighted: true
  },
  {
    name: "Awakening",
    subtitle: "7-Day Immersion",
    price: "R$1,800",
    description: "Deep dive into consciousness",
    features: [
      "Five sacred ceremonies",
      "Comprehensive preparation",
      "Daily yoga & meditation",
      "Plant medicine dieta",
      "Private accommodation",
      "All meals included",
      "River ceremony",
      "Personal integration plan"
    ],
    highlighted: false
  }
];

const PackageCard = ({ pkg, index, whatsappLink }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className={`relative rounded-3xl overflow-hidden ${
        pkg.highlighted ? 'lg:scale-110 z-10' : ''
      }`}
      data-testid={`package-card-${index}`}
    >
      {/* Highlighted badge */}
      {pkg.highlighted && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-egyptian-gold to-yellow-500 py-2 text-center">
          <span className="font-heading font-bold text-jungle-dark text-sm uppercase tracking-wider flex items-center justify-center gap-2">
            <Star className="w-4 h-4" fill="currentColor" />
            Most Popular
            <Star className="w-4 h-4" fill="currentColor" />
          </span>
        </div>
      )}

      <div className={`glass-card h-full p-8 ${pkg.highlighted ? 'pt-16 border-2 border-egyptian-gold/50' : 'border border-neon-cyan/20'}`}>
        {/* Package Header */}
        <div className="text-center mb-8">
          <p className="font-accent text-neon-cyan text-xs tracking-[0.2em] mb-2">
            {pkg.subtitle}
          </p>
          <h3 className="font-heading font-bold text-3xl text-white uppercase tracking-wide mb-2">
            {pkg.name}
          </h3>
          <p className="font-body text-gray-500 text-sm mb-6">
            {pkg.description}
          </p>
          
          {/* Price */}
          <div className="mb-6">
            <span className={`font-heading font-bold text-5xl ${pkg.highlighted ? 'neon-gold' : 'text-white'}`}>
              {pkg.price}
            </span>
            <span className="font-body text-gray-500 text-sm block mt-1">
              per person
            </span>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-4 mb-8">
          {pkg.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                pkg.highlighted ? 'bg-egyptian-gold/20' : 'bg-neon-cyan/20'
              }`}>
                <Check className={`w-3 h-3 ${pkg.highlighted ? 'text-egyptian-gold' : 'text-neon-cyan'}`} />
              </div>
              <span className="font-body text-gray-300 text-sm">
                {feature}
              </span>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-heading font-semibold uppercase tracking-wider transition-transform duration-300 hover:scale-105 ${
            pkg.highlighted
              ? 'bg-gradient-to-r from-egyptian-gold to-yellow-500 text-jungle-dark'
              : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
          }`}
          data-testid={`package-cta-${index}`}
        >
          <MessageCircle className="w-5 h-5" />
          Book Now
        </a>
      </div>
    </motion.div>
  );
};

export const PackagesSection = ({ whatsappLink }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="packages" className="py-24 relative" data-testid="packages-section">
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-jungle-dark to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-jungle-dark to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-neon-magenta text-sm tracking-[0.3em] mb-4">
            CHOOSE YOUR PATH
          </p>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight text-white mb-6">
            Sacred <span className="neon-gold">Packages</span>
          </h2>
          <p className="font-body text-gray-400 text-lg max-w-2xl mx-auto">
            Each package is thoughtfully designed to provide the optimal environment for your transformation. 
            Choose the journey that resonates with your soul.
          </p>
        </motion.div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-4 items-stretch">
          {packages.map((pkg, index) => (
            <PackageCard key={index} pkg={pkg} index={index} whatsappLink={whatsappLink} />
          ))}
        </div>

        {/* Bottom Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center font-body text-gray-500 text-sm mt-12"
        >
          * All prices in Brazilian Reais (BRL). Custom packages available upon request. 
          Contact us for group rates.
        </motion.p>
      </div>
    </section>
  );
};
